import {
  useEffect,
  useMemo,
  useRef,
  type CSSProperties,
  type MouseEvent,
} from "react";

import { cn } from "../../design-system/cn.js";
import {
  DAY_ABBREVIATIONS,
  DAY_NAMES,
  formatClock,
  SECONDS_PER_DAY,
  type GridBlock,
} from "./grid-model.js";

/**
 * A week of programming, drawn as a week.
 *
 * Seven columns of twenty-four hours, which is the shape the thing actually has —
 * a list of "Mon–Fri 06:00–10:00" rows makes the operator rebuild the week in his
 * head every time he reads it. Blocks are placed as a fraction of the day rather than
 * in pixels, so one hour is stated once as a token and the grid is the same at both
 * breakpoints; at 360 the seven columns become one, chosen with the day pills above,
 * because seven columns in 360 pixels is seven columns of nothing.
 *
 * A slot that runs past midnight is drawn as two blocks that each say they continue,
 * because that is where its audio actually is in the week.
 */

const SLOT_TONES = ["a", "b", "c", "d", "e", "f"] as const;

/**
 * A tone per playlist, assigned by the order the playlists appear in this schedule
 * rather than hashed from the id. Hashing is stable but arbitrary — two playlists can
 * land next to each other in hue and be told apart only by reading them, which is the
 * one thing colour on a grid is there to save. Going round the six in order means the
 * first two playlists of any schedule are always as far apart as the rotation allows.
 */
export function toneAssignment(
  playlistIds: readonly string[],
): (playlistId: string) => string {
  const tones = new Map<string, string>();
  for (const playlistId of playlistIds) {
    if (tones.has(playlistId)) continue;
    tones.set(playlistId, SLOT_TONES[tones.size % SLOT_TONES.length] ?? "a");
  }
  return (playlistId: string) => tones.get(playlistId) ?? "a";
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);

export function WeekGrid({
  blocks,
  days,
  nowMarker,
  onCreate,
  onOpen,
  playlistName,
  toneOf,
}: {
  blocks: readonly GridBlock[];
  /** Which day columns to draw. One at 360, all seven at 1440. */
  days: readonly number[];
  /** Where the schedule's own clock is right now, or null when it is not this week. */
  nowMarker: { day: number; seconds: number } | null;
  onCreate: (day: number, seconds: number) => void;
  onOpen: (slotId: string) => void;
  playlistName: (playlistId: string) => string;
  /** Which of the six accent rotations each playlist draws in. */
  toneOf: (playlistId: string) => string;
}) {
  const scroller = useRef<HTMLDivElement | null>(null);
  const byDay = useMemo(() => {
    const grouped = new Map<number, GridBlock[]>();
    for (const block of blocks) {
      const list = grouped.get(block.day) ?? [];
      list.push(block);
      grouped.set(block.day, list);
    }
    return grouped;
  }, [blocks]);

  /**
   * The week opens where the operator is, not at midnight. A grid that starts at
   * 00:00 shows six empty hours before anything a station does in the morning.
   */
  const openAtSeconds = nowMarker?.seconds ?? 6 * 3_600;
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    const full = element.scrollHeight;
    element.scrollTop = Math.max(
      0,
      (openAtSeconds / SECONDS_PER_DAY) * full - element.clientHeight / 3,
    );
    // Only on mount: after that, where the operator has scrolled to is his.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function createAt(event: MouseEvent<HTMLDivElement>, day: number) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const fraction = (event.clientY - bounds.top) / bounds.height;
    const hour = Math.max(0, Math.min(23, Math.floor(fraction * 24)));
    onCreate(day, hour * 3_600);
  }

  return (
    <div className="overflow-hidden rounded-md border border-border bg-surface">
      <div
        className="grid border-b border-border"
        style={{
          gridTemplateColumns: `3rem repeat(${String(days.length)}, minmax(0, 1fr))`,
        }}
      >
        <span />
        {days.map((day) => (
          <span
            key={day}
            className="border-l border-border px-2 py-2 text-center font-mono text-[0.625rem] tracking-[0.1em] text-ink-3 uppercase"
          >
            {days.length === 1 ? DAY_NAMES[day] : DAY_ABBREVIATIONS[day]}
          </span>
        ))}
      </div>

      <div className="max-h-[34rem] overflow-y-auto" ref={scroller}>
        <div
          className="grid"
          style={{
            gridTemplateColumns: `3rem repeat(${String(days.length)}, minmax(0, 1fr))`,
            height: "calc(var(--hour-h) * 24)",
          }}
        >
          <div className="relative">
            {HOURS.map((hour) => (
              <span
                key={hour}
                className="absolute right-2 -translate-y-1/2 font-mono text-[0.5625rem] text-ink-3 tabular-nums"
                style={{ top: `${String((hour / 24) * 100)}%` }}
              >
                {hour === 0 ? "" : formatClock(hour * 3_600)}
              </span>
            ))}
          </div>

          {days.map((day) => (
            <div
              key={day}
              className="relative border-l border-border bg-[repeating-linear-gradient(to_bottom,transparent_0,transparent_calc(var(--hour-h)_-_1px),var(--border)_calc(var(--hour-h)_-_1px),var(--border)_var(--hour-h))]"
              onClick={(event) => {
                createAt(event, day);
              }}
              role="presentation"
            >
              {(byDay.get(day) ?? []).map((block) => (
                <SlotBlock
                  key={block.key}
                  block={block}
                  onOpen={onOpen}
                  playlistName={playlistName}
                  tone={toneOf(block.playlistId)}
                />
              ))}
              {nowMarker?.day === day ? (
                <span
                  aria-label="Now, in this schedule's timezone"
                  className="pointer-events-none absolute inset-x-0 z-20 h-px bg-accent"
                  style={{
                    top: `${String((nowMarker.seconds / SECONDS_PER_DAY) * 100)}%`,
                  }}
                >
                  <span className="absolute -top-[3px] -left-[3px] size-1.5 rounded-full bg-accent" />
                </span>
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SlotBlock({
  block,
  onOpen,
  playlistName,
  tone,
}: {
  block: GridBlock;
  onOpen: (slotId: string) => void;
  playlistName: (playlistId: string) => string;
  tone: string;
}) {
  const top = (block.fromSeconds / SECONDS_PER_DAY) * 100;
  const height =
    ((block.toSeconds - block.fromSeconds) / SECONDS_PER_DAY) * 100;
  const width = 100 / block.laneCount;
  const name = playlistName(block.playlistId);

  return (
    <button
      className={cn(
        "absolute z-10 flex flex-col items-stretch justify-start overflow-hidden rounded-sm border px-1.5 py-1 text-left transition-colors",
        // Mixed with transparent rather than with the surface: oklch interpolates
        // hue, so mixing a warm tone into a cool surface drags it round the wheel and
        // six distinct tones arrive on screen as six shades of the surface. Over
        // transparent there is no second hue to interpolate against, which is the same
        // reason --accent-soft is written this way.
        "border-[color-mix(in_oklch,var(--slot)_55%,transparent)] bg-[color-mix(in_oklch,var(--slot)_20%,transparent)] hover:bg-[color-mix(in_oklch,var(--slot)_32%,transparent)]",
        !block.enabled && "opacity-45",
      )}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(block.slotId);
      }}
      style={
        {
          top: `${String(top)}%`,
          height: `max(1.25rem, ${String(height)}%)`,
          left: `${String(block.lane * width)}%`,
          width: `calc(${String(width)}% - 2px)`,
          // Set here rather than in a class because which of the six a playlist gets is
          // data, and the six themselves are tokens derived from the one accent.
          "--slot": `var(--slot-${tone})`,
        } as CSSProperties
      }
      title={`${name} · ${formatClock(block.fromSeconds)}–${formatClock(block.toSeconds)}${block.enabled ? "" : " · disabled"}`}
      type="button"
    >
      <span className="block truncate text-[0.6875rem] leading-4 font-medium text-ink">
        {block.continuesFromPreviousDay ? "↳ " : ""}
        {name}
        {block.continuesIntoNextDay ? " ↴" : ""}
      </span>
      <span className="block truncate font-mono text-[0.5625rem] text-ink-2">
        {formatClock(block.fromSeconds)}–{formatClock(block.toSeconds)}
        {block.priority === 0 ? "" : ` · p${String(block.priority)}`}
      </span>
    </button>
  );
}
